import { Inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  fromEvent,
  map,
  mergeMap,
  Observable,
  startWith,
  Subject,
  withLatestFrom,
} from 'rxjs';
import { AutocompleteFiltersWrapperElement } from './autocomplete-filters-wrapper/autocomplete-filters-wrapper.preact.component';
import { filter } from 'rxjs/operators';
import { isPresent, getCurrentUsername } from '@mode/shared/util-js';
import { MenuItem } from '@mode/shared/ui';
import { BaseTag } from '@algolia/autocomplete-plugin-tags';
import { AutocompleteTagsService, FacetTagProps } from './autocomplete/autocomplete-tags.service';
import { differenceBy } from 'lodash';
import { DOCUMENT } from '@angular/common';
import {
  CONTENT_TYPE_CHANGE_EVENT_TYPE,
  CREATED_BY_ME_CHANGE_EVENT_TYPE,
  MATCH_ON_CHANGE_EVENT_TYPE,
} from './autocomplete-filters/autocomplete-filters.component';
import { GlobalSearchStore } from './global-search.store';

type FacetTag = BaseTag<FacetTagProps>;
type FacetTagOrNull = FacetTag | null;

export type ContentTypeChangeEvent = { detail: MenuItem | MenuItem[] };
export type MatchOnChangeEvent = { detail: MenuItem | MenuItem[] };
export type CreatedByMeChangeEvent = { detail: MenuItem<boolean> };

@Injectable({
  providedIn: 'root',
})
export class GlobalSearchService {
  public filtersWrapperCurrentRefSubject$ = new BehaviorSubject<AutocompleteFiltersWrapperElement | null>(null);
  public filtersWrapperCurrentElement$ = this.filtersWrapperCurrentRefSubject$.pipe(
    filter(isPresent),
    filter((currentRef) => currentRef !== null),
    // todo avoid typecasting
    map((currentRef) => currentRef.base as Element)
  );
  public contentTypeChangeEvent$: Observable<ContentTypeChangeEvent>;
  public matchOnChangeEvent$: Observable<MatchOnChangeEvent>;
  public createdByMeChangeEvent$: Observable<CreatedByMeChangeEvent>;
  public contentTypeTags$: Observable<FacetTag[]>;
  public matchOnTags$: Observable<FacetTag[]>;
  public createdByMeTags$: Observable<BaseTag<FacetTagProps> | null>;
  public tagStateUpdate$: Observable<[FacetTag[], FacetTag[], FacetTagOrNull]>;
  public tagStateUpdateSubject$ = new Subject<[FacetTag[], FacetTag[], FacetTagOrNull]>();

  constructor(
    private autocompleteTagsService: AutocompleteTagsService,
    public store: GlobalSearchStore,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.contentTypeChangeEvent$ = this.filtersWrapperCurrentElement$.pipe(
      mergeMap((elem) => fromEvent<ContentTypeChangeEvent>(elem, CONTENT_TYPE_CHANGE_EVENT_TYPE))
    );

    this.matchOnChangeEvent$ = this.filtersWrapperCurrentElement$.pipe(
      mergeMap((elem) => fromEvent<MatchOnChangeEvent>(elem, MATCH_ON_CHANGE_EVENT_TYPE))
    );

    this.createdByMeChangeEvent$ = this.filtersWrapperCurrentElement$.pipe(
      mergeMap((elem) => fromEvent<CreatedByMeChangeEvent>(elem, CREATED_BY_ME_CHANGE_EVENT_TYPE))
    );

    this.contentTypeTags$ = this.store.selectedContentTypeItems$.pipe(
      withLatestFrom(this.store.contentTypeItems$),
      map(([selection, contentTypeItems]) => this.menuItemDifference(contentTypeItems, selection)),
      startWith([]),
      map((selections) =>
        selections.map((selection) => this.autocompleteTagsService.createTag(selection, 'reportType'))
      )
    );

    this.matchOnTags$ = this.store.selectedMatchOnItems$.pipe(
      map((selections) => selections.map((selection) => this.autocompleteTagsService.createTag(selection, '', 'attr')))
    );

    this.createdByMeTags$ = this.createdByMeChangeEvent$.pipe(
      map((event) => event.detail),
      startWith<MenuItem<boolean>>({ name: 'created_by_me', value: false }),
      map((selection) => this.getCreatorUsernameTag(selection))
    );

    this.tagStateUpdate$ = combineLatest([this.contentTypeTags$, this.matchOnTags$, this.createdByMeTags$]);

    // It's important to subscribe to the subject here before the subject is
    // subscribed to the update observable so as to not miss the initial emitted value
    this.tagStateUpdateSubject$.subscribe(([contentTypeTags, matchOnTags, createdByMeTags]) => {
      this.updateTagState([contentTypeTags, matchOnTags, createdByMeTags].flat(1));
    });

    this.tagStateUpdate$.subscribe(this.tagStateUpdateSubject$);

    this.contentTypeChangeEvent$.subscribe((event) => {
      this.store.updateSelectedContentTypeOptions([event.detail].flat(1));
    });

    this.matchOnChangeEvent$.subscribe((event) => {
      this.store.updateSelectedMatchOnOptions([event.detail].flat(1));
    });
  }

  private getCreatorUsernameTag(selection: MenuItem<boolean>) {
    const value = getCurrentUsername(this.document);

    if (!value || !selection.value) {
      return null;
    }

    return this.autocompleteTagsService.createTag({ ...selection, value }, 'reportCreatorUsername');
  }

  public menuItemDifference(base: MenuItem[], selected: MenuItem[]): MenuItem[] {
    return differenceBy(base, selected, (item) => item.value);
  }

  public updateFiltersWrapperRef(elem: AutocompleteFiltersWrapperElement | null, force = false) {
    // update only once
    if (!this.filtersWrapperCurrentRefSubject$.getValue() || force) {
      this.filtersWrapperCurrentRefSubject$.next(elem);
    }
  }

  public updateTagState(tags: FacetTagOrNull[]) {
    const isTag = (tag: FacetTagOrNull): tag is FacetTag => tag != null;
    this.autocompleteTagsService.setTags(tags.filter(isTag));
  }
}
